/// Web Audio playback for a Morse timeline.
///
/// Two oscillators isn't worth the complexity for a single tone — we
/// gate one oscillator's gain via scheduled AudioParam ramps. That
/// gives us click-free on/off transitions cheaply.

import type { Segment } from "./timing";

export interface PlayOptions {
  /// Tone frequency in Hz. Defaults to the conventional 700 Hz used
  /// by maritime CW practice (a clear midrange that cuts through
  /// background noise without being shrill).
  frequencyHz?: number;
  /// Peak gain on each "on" segment, 0–1. Default 0.2 — low enough
  /// to not blow out laptop speakers, high enough to be clearly
  /// audible.
  gain?: number;
  /// Ramp time at on/off transitions, ms. 4ms eliminates the
  /// characteristic "click" of an instant gate without smearing the
  /// dit/dah boundary perceptually.
  rampMs?: number;
  /// Inject your own AudioContext (for tests). Defaults to a new
  /// `AudioContext` on first call.
  audioContext?: AudioContext;
}

export interface Playback {
  /// Resolves when the timeline finishes playing.
  done: Promise<void>;
  /// Stop playback immediately. Idempotent.
  stop: () => void;
}

/// Play a Morse timeline. Returns a `Playback` handle the caller can
/// await or stop. Resumes a suspended AudioContext if the page hasn't
/// had a user interaction yet (browsers auto-suspend until then).
export function play(
  timeline: readonly Segment[],
  options: PlayOptions = {},
): Playback {
  const ctx = options.audioContext ?? new AudioContext();
  const freq = options.frequencyHz ?? 700;
  const peak = options.gain ?? 0.2;
  const ramp = (options.rampMs ?? 4) / 1000;

  void ctx.resume();

  const osc = ctx.createOscillator();
  const gainNode = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  osc.connect(gainNode).connect(ctx.destination);

  gainNode.gain.setValueAtTime(0, ctx.currentTime);

  let cursorSec = ctx.currentTime;
  for (const seg of timeline) {
    const start = cursorSec;
    const end = start + seg.durationMs / 1000;
    if (seg.on) {
      gainNode.gain.setValueAtTime(0, start);
      gainNode.gain.linearRampToValueAtTime(peak, start + ramp);
      gainNode.gain.setValueAtTime(peak, end - ramp);
      gainNode.gain.linearRampToValueAtTime(0, end);
    }
    cursorSec = end;
  }

  osc.start(ctx.currentTime);
  osc.stop(cursorSec + 0.05);

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    try {
      osc.stop(ctx.currentTime);
    } catch {
      /* already stopped */
    }
  };

  const done = new Promise<void>((resolve) => {
    osc.addEventListener("ended", () => resolve(), { once: true });
  });

  return { done, stop };
}
